import { AuthSettingsApi } from '@/entities/system/setting';
import { LDAPApi } from '@/entities/user/user';
import { OIDCApi } from '@/entities/user/user';
import { UsersAPI } from '@/entities/user/user';
import { DockerUrlsApi } from '@/entities/system/docker-tool';
import { DockerToolSettingsApi } from '@/entities/system/setting';
import { ReceiversApi } from '@/entities/notifications/receiver';
import { SendersApi } from '@/entities/notifications/sender';
import { NotificationSettingsApi } from '@/entities/system/setting';
import { TimeIntervalsApi } from '@/entities/notifications/time-interval';
import { ExportersAPI } from '@/entities/monitoring/exporter';
import { JobsAPI } from '@/entities/monitoring/job';
import { MonitoringTemplatesApi } from '@/entities/monitoring/monitoring-template';
import { MonitoringSettingsApi } from '@/entities/system/setting';
import { VMAgentsAPI } from '@/entities/monitoring/vm-agent';
import { VMAlertsAPI } from '@/entities/monitoring/vm-alert';
import { AccessRulesDashboardsApi } from '@/entities/user/access-rule';
import { AccessRulesHiApi } from '@/entities/user/access-rule';
import { AccessRulesTQLApi } from '@/entities/user/access-rule';
import { AccessRulesApi } from '@/entities/user/access-rule';
import { DashboardAPI } from '@/entities/dashboards/dashboard';
import { EnrichmentPlansAPI } from '@/entities/srm/enrichment';
import { InterfacesApi } from '@/entities/user/access-rule';
import { PluginsApi } from '@/entities/system/plugin';
import { SRMSettingsApi } from '@/entities/system/setting';
import { TQLQueriesApi } from '@/entities/srm/tql-query';
import { TaskToolSettingsApi } from '@/entities/system/setting';
import { attributesQueryConfig } from '@/entities/srm/ci';
import { ciTypesListQueryConfig } from '@/entities/srm/ci';
import { hiTypesQueryConfig } from '@/entities/monitoring/hi';
import { userAttributesQueryConfig } from '@/entities/user/user-attribute';
import {
  ACCESS_DASHBOARDS_V4,
  ACCESS_HEALTH_INDICATOR,
  ACCESS_NAVIGATION,
  ACCESS_SRM,
  ACCESS_TQL,
  DASHBOARDS,
  SRM,
  SETTINGS_ATTRIBUTES,
  SETTINGS_CI_TYPES,
  SETTINGS_COMPONENTS,
  SETTINGS_ENRICHMENT,
  SETTINGS_GLOBAL,
  SETTINGS_HEALTH_INDICATORS,
  SETTINGS_LDAP,
  SETTINGS_MONITORING_EXPORTERS,
  SETTINGS_MONITORING_JOBS,
  SETTINGS_MONITORING_TEMPLATES,
  SETTINGS_MY_RECEIVERS,
  SETTINGS_OIDC,
  SETTINGS_PLUGINS,
  SETTINGS_SENDERS,
  SETTINGS_TIME_INTERVAL,
  SETTINGS_USER_ATTRIBUTES,
  SETTINGS_VM_AGENTS,
  SETTINGS_VM_ALERTS,
  USERS_REGISTRY,
} from '@/shared/config/routes';
import { Page } from '@/shared/api/pagination';
import { Attribute } from '@/entities/srm/ci';
import { CITypeListItem } from '@/entities/srm/ci';
import { HIType } from '@/entities/monitoring/hi';
import { getLdapId } from '@/entities/user/user';
import { UserAttribute } from '@/entities/user/user-attribute';

import type { AppSearchAction, AppSearchProvider } from '../types';
import {
  createPageSearchProvider,
  pageSearchParams,
  toItemAction,
} from './create-page-search';

const buildCitTree = (items: CITypeListItem[]): AppSearchAction[] => {
  const ids = new Set(items.map((item) => item.id));
  const byParent = new Map<string, CITypeListItem[]>();

  for (const item of items) {
    if (item.parent_id && ids.has(item.parent_id)) {
      const siblings = byParent.get(item.parent_id) ?? [];
      siblings.push(item);
      byParent.set(item.parent_id, siblings);
    }
  }

  const toAction = (item: CITypeListItem): AppSearchAction => {
    const children = (byParent.get(item.id) ?? []).map(toAction);
    const label = item.label || item.name;

    return toItemAction({
      id: 'cit',
      label,
      description: item.name,
      keywords: [item.label, item.name],
      children,
      page: SETTINGS_CI_TYPES,
      searchValue: label,
      item,
      select: 'id',
    });
  };

  return items
    .filter((item) => !item.parent_id || !ids.has(item.parent_id))
    .map(toAction);
};

const itemsFromPage = <T>(data?: { items?: T[] } | T[] | null): T[] => {
  if (!data) {
    return [];
  }

  return Array.isArray(data) ? data : (data.items ?? []);
};

export const tqlSearchProvider = createPageSearchProvider({
  id: 'tql',
  attachTo: SRM,
  mode: 'page',
  select: 'name',
  selectParam: 'query_name',
  search: async (query, { navigate }, signal) => {
    const { data } = await TQLQueriesApi.getTQLQueries({
      signal,
      params: pageSearchParams(query, ['label', 'name']),
    });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'tql',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SRM,
        searchValue: item.label || item.name,
        item,
        select: 'name',
        selectParam: 'query_name',
      }),
    );
  },
});

export const dashboardSearchProvider = createPageSearchProvider({
  id: 'dashboards',
  attachTo: DASHBOARDS,
  mode: 'page',
  select: 'name',
  search: async (query, { navigate }, signal) => {
    const { data } = await DashboardAPI.getDashboardList(
      pageSearchParams(query, ['title', 'name']),
      { signal },
    );

    return itemsFromPage(data).map((dashboard) =>
      toItemAction({
        id: 'dashboard',
        label: dashboard.title,
        description: dashboard.name,
        keywords: [dashboard.title, dashboard.name],
        page: DASHBOARDS,
        searchValue: dashboard.title,
        item: dashboard,
        select: 'name',
      }),
    );
  },
});

export const attributeSearchProvider = createPageSearchProvider({
  id: 'attributes',
  attachTo: SETTINGS_ATTRIBUTES,
  mode: 'cache',
  select: 'name',
  cacheQuery: attributesQueryConfig,
  mapCache: (data, { navigate }) =>
    itemsFromPage(data as Page<Attribute>).map((attribute) =>
      toItemAction({
        id: 'attribute',
        label: attribute.label || attribute.name,
        description: attribute.name,
        keywords: [attribute.label, attribute.name],
        page: SETTINGS_ATTRIBUTES,
        searchValue: attribute.name,
        item: attribute,
        select: 'name',
      }),
    ),
});

export const userAttributeSearchProvider = createPageSearchProvider({
  id: 'user-attributes',
  attachTo: SETTINGS_USER_ATTRIBUTES,
  mode: 'cache',
  select: 'name',
  cacheQuery: userAttributesQueryConfig,
  mapCache: (data, { navigate }) =>
    itemsFromPage(data as Page<UserAttribute>).map((attribute) =>
      toItemAction({
        id: 'user-attribute',
        label: attribute.label || attribute.name,
        description: attribute.name,
        keywords: [attribute.label, attribute.name],
        page: SETTINGS_USER_ATTRIBUTES,
        searchValue: attribute.name,
        item: attribute,
        select: 'name',
      }),
    ),
});

export const healthIndicatorSearchProvider = createPageSearchProvider({
  id: 'health-indicators',
  attachTo: SETTINGS_HEALTH_INDICATORS,
  mode: 'cache',
  select: 'id',
  cacheQuery: hiTypesQueryConfig,
  mapCache: (data, { navigate }) =>
    (Array.isArray(data) ? (data as HIType[]) : []).map((item) =>
      toItemAction({
        id: 'hi',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_HEALTH_INDICATORS,
        searchValue: item.name || item.label || '',
        item,
        select: 'id',
      }),
    ),
});

export const userSearchProvider = createPageSearchProvider({
  id: 'users',
  attachTo: USERS_REGISTRY,
  mode: 'page',
  select: 'id',
  search: async (query, { navigate }, signal) => {
    const { data } = await UsersAPI.getUsers({
      signal,
      params: pageSearchParams(query, ['surname', 'name', 'login']),
    });

    return itemsFromPage(data).map((user) => {
      const label =
        [user.surname, user.name].filter(Boolean).join(' ') || user.login;

      return toItemAction({
        id: 'user',
        label,
        description: user.login,
        keywords: [user.surname, user.name, user.login],
        page: USERS_REGISTRY,
        searchValue: user.login,
        item: user,
        select: 'id',
      });
    });
  },
});

export const citSearchProvider = createPageSearchProvider({
  id: 'cit',
  attachTo: SETTINGS_CI_TYPES,
  mode: 'cache',
  select: 'id',
  cacheQuery: ciTypesListQueryConfig,
  mapCache: (data, { navigate }) =>
    buildCitTree(Array.isArray(data) ? (data as CITypeListItem[]) : []),
});

export const enrichmentSearchProvider = createPageSearchProvider({
  id: 'enrichment',
  attachTo: SETTINGS_ENRICHMENT,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await EnrichmentPlansAPI.getEnrichmentItems(
      { size: -1 },
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'enrichment',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_ENRICHMENT,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const exporterSearchProvider = createPageSearchProvider({
  id: 'exporters',
  attachTo: SETTINGS_MONITORING_EXPORTERS,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await ExportersAPI.getExporters({ size: -1 }, { signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'exporter',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_MONITORING_EXPORTERS,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const jobSearchProvider = createPageSearchProvider({
  id: 'jobs',
  attachTo: SETTINGS_MONITORING_JOBS,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await JobsAPI.getJobs({ size: -1 }, { signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'job',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_MONITORING_JOBS,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const vmAgentSearchProvider = createPageSearchProvider({
  id: 'vm-agents',
  attachTo: SETTINGS_VM_AGENTS,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await VMAgentsAPI.getVMAgents({ size: -1 }, { signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'vmagent',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_VM_AGENTS,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const vmAlertSearchProvider = createPageSearchProvider({
  id: 'vm-alerts',
  attachTo: SETTINGS_VM_ALERTS,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await VMAlertsAPI.getVMAlerts({ size: -1 }, { signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'vmalert',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name],
        page: SETTINGS_VM_ALERTS,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const monitoringTemplateSearchProvider = createPageSearchProvider({
  id: 'monitoring-templates',
  attachTo: SETTINGS_MONITORING_TEMPLATES,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await MonitoringTemplatesApi.getTemplates(
      { size: -1 },
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'template',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name, item.description],
        page: SETTINGS_MONITORING_TEMPLATES,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const receiverSearchProvider = createPageSearchProvider({
  id: 'receivers',
  attachTo: SETTINGS_MY_RECEIVERS,
  mode: 'client',
  select: 'id',
  search: async (_query, _runtime, signal) => {
    const { data } = await ReceiversApi.getReceivers({ signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'receiver',
        label: item.name,
        keywords: [item.name, item.id],
        page: SETTINGS_MY_RECEIVERS,
        searchValue: item.name,
        item,
        select: 'id',
      }),
    );
  },
});

export const ldapSearchProvider = createPageSearchProvider({
  id: 'ldap',
  attachTo: SETTINGS_LDAP,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await LDAPApi.getConnections({ size: -1 }, { signal });

    return itemsFromPage(data).map((item) => {
      const id = getLdapId(item) || item.name;

      return toItemAction({
        id: 'ldap',
        label: item.display_name || item.name,
        description: item.name,
        keywords: [item.display_name, item.name],
        page: SETTINGS_LDAP,
        searchValue: item.display_name || item.name,
        selectedId: id,
        select: 'name',
      });
    });
  },
});

const createAccessRuleSearchProvider = ({
  id,
  attachTo,
  search,
}: {
  id: string;
  attachTo: string;
  search: AppSearchProvider['search'];
}) =>
  createPageSearchProvider({
    id,
    attachTo,
    mode: 'page',
    select: 'id',
    search,
  });

const toAccessRuleAction = (
  idPrefix: string,
  attachTo: string,
  item: { id?: string; name?: string; label?: string },
) =>
  toItemAction({
    id: idPrefix,
    label: item.label || item.name || '',
    description: item.name,
    keywords: [item.label, item.name],
    page: attachTo,
    searchValue: item.name || item.label || '',
    item,
    select: 'id',
  });

export const accessSrmSearchProvider = createAccessRuleSearchProvider({
  id: 'access-srm',
  attachTo: ACCESS_SRM,
  search: async (query, _runtime, signal) => {
    const { data } = await AccessRulesApi.getRuleListBrief(
      pageSearchParams(query, ['name']),
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toAccessRuleAction('access-srm', ACCESS_SRM, item),
    );
  },
});

export const accessHiSearchProvider = createAccessRuleSearchProvider({
  id: 'access-hi',
  attachTo: ACCESS_HEALTH_INDICATOR,
  search: async (query, _runtime, signal) => {
    const { data } = await AccessRulesHiApi.getRulesBrief(
      pageSearchParams(query, ['name']),
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toAccessRuleAction('access-hi', ACCESS_HEALTH_INDICATOR, item),
    );
  },
});

export const accessNavigationSearchProvider = createAccessRuleSearchProvider({
  id: 'access-navigation',
  attachTo: ACCESS_NAVIGATION,
  search: async (query, _runtime, signal) => {
    const { data } = await InterfacesApi.getRuleListBrief(
      pageSearchParams(query, ['name']),
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toAccessRuleAction('access-navigation', ACCESS_NAVIGATION, item),
    );
  },
});

export const accessDashboardsSearchProvider = createAccessRuleSearchProvider({
  id: 'access-dashboards',
  attachTo: ACCESS_DASHBOARDS_V4,
  search: async (query, _runtime, signal) => {
    const { data } = await AccessRulesDashboardsApi.getRulesBrief(
      pageSearchParams(query, ['name']),
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toAccessRuleAction('access-dashboards', ACCESS_DASHBOARDS_V4, item),
    );
  },
});

export const accessTqlSearchProvider = createAccessRuleSearchProvider({
  id: 'access-tql',
  attachTo: ACCESS_TQL,
  search: async (query, _runtime, signal) => {
    const { data } = await AccessRulesTQLApi.getRulesBrief(
      pageSearchParams(query, ['name', 'label']),
      { signal },
    );

    return itemsFromPage(data).map((item) =>
      toAccessRuleAction('access-tql', ACCESS_TQL, item),
    );
  },
});

export const oidcSearchProvider = createPageSearchProvider({
  id: 'oidc',
  attachTo: SETTINGS_OIDC,
  mode: 'client',
  select: 'name',
  search: async (_query, { navigate }, signal) => {
    const { data } = await OIDCApi.getProviders({
      signal,
      params: { size: -1 },
    });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'oidc',
        label: item.display_name || item.name,
        description: item.name,
        keywords: [item.display_name, item.name],
        page: SETTINGS_OIDC,
        searchValue: item.display_name || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const timeIntervalSearchProvider = createPageSearchProvider({
  id: 'time-intervals',
  attachTo: SETTINGS_TIME_INTERVAL,
  mode: 'client',
  select: 'id',
  search: async (_query, _runtime, signal) => {
    const { data } = await TimeIntervalsApi.getTimeIntervals({ signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'time-interval',
        label: item.name,
        keywords: [item.name, item.id],
        page: SETTINGS_TIME_INTERVAL,
        searchValue: item.name,
        item,
        select: 'id',
      }),
    );
  },
});

export const senderSearchProvider = createPageSearchProvider({
  id: 'senders',
  attachTo: SETTINGS_SENDERS,
  mode: 'client',
  select: 'id',
  search: async (_query, _runtime, signal) => {
    const { data } = await SendersApi.getSenders({ signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'sender',
        label: item.name,
        keywords: [item.name, item.id],
        page: SETTINGS_SENDERS,
        searchValue: item.name,
        item,
        select: 'id',
      }),
    );
  },
});

export const componentSearchProvider = createPageSearchProvider({
  id: 'components',
  attachTo: SETTINGS_COMPONENTS,
  mode: 'client',
  select: 'name',
  search: async (_query, _runtime, signal) => {
    const { data } = await DockerUrlsApi.getUrls({ signal });

    return itemsFromPage(data).map((item) =>
      toItemAction({
        id: 'component',
        label: item.label || item.name,
        description: item.name,
        keywords: [item.label, item.name, item.url],
        page: SETTINGS_COMPONENTS,
        searchValue: item.label || item.name,
        item,
        select: 'name',
      }),
    );
  },
});

export const pluginSearchProvider = createPageSearchProvider({
  id: 'plugins',
  attachTo: SETTINGS_PLUGINS,
  mode: 'client',
  select: 'name',
  search: async (_query, _runtime, signal) => {
    const [{ data: downloaded }, { data: installed }] = await Promise.all([
      PluginsApi.getDownloadedPluginsList({ signal }),
      PluginsApi.getInstalledPluginsList({ signal }),
    ]);

    return [
      ...itemsFromPage(installed).map((item) =>
        toItemAction({
          id: 'plugin-installed',
          label: item.display_name || item.name,
          description: item.name,
          keywords: [
            item.display_name,
            item.name,
            item.arch_filename,
            item.version,
          ],
          page: SETTINGS_PLUGINS,
          searchValue: item.display_name || item.name,
          selectedId: item.name,
          select: 'name',
        }),
      ),
      ...itemsFromPage(downloaded).map((item) =>
        toItemAction({
          id: 'plugin-downloaded',
          label: item.filename,
          description: item.version,
          keywords: [item.filename, item.version],
          page: SETTINGS_PLUGINS,
          searchValue: item.filename,
          selectedId: item.filename,
          select: 'name',
        }),
      ),
    ];
  },
});

const GLOBAL_SETTINGS_SOURCES = [
  { service: 'auth', api: AuthSettingsApi },
  { service: 'srm', api: SRMSettingsApi },
  { service: 'monitoring', api: MonitoringSettingsApi },
  { service: 'notification', api: NotificationSettingsApi },
  { service: 'docker_tool', api: DockerToolSettingsApi },
  { service: 'task_tool', api: TaskToolSettingsApi },
] as const;

export const globalSettingsSearchProvider = createPageSearchProvider({
  id: 'global-settings',
  attachTo: SETTINGS_GLOBAL,
  mode: 'client',
  select: 'id',
  search: async (_query, _runtime, signal) => {
    const pages = await Promise.all(
      GLOBAL_SETTINGS_SOURCES.map(async ({ service, api }) => {
        const { data } = await api.getAllSettings({ size: -1 }, { signal });

        return { service, items: itemsFromPage(data) };
      }),
    );

    return pages.flatMap(({ service, items }) =>
      items.map((item) =>
        toItemAction({
          id: `global-setting:${service}`,
          label: item.name,
          description: item.description || service,
          keywords: [item.name, item.description, item.section, service],
          page: SETTINGS_GLOBAL,
          searchValue: item.name,
          item,
          select: 'id',
          href: `${SETTINGS_GLOBAL}?name=${service}&selected=${encodeURIComponent(item.id)}`,
          searchHref: `${SETTINGS_GLOBAL}?name=${service}&search=${encodeURIComponent(item.name)}`,
        }),
      ),
    );
  },
});

export const PAGE_SEARCH_PROVIDERS: AppSearchProvider[] = [
  tqlSearchProvider,
  dashboardSearchProvider,
  accessSrmSearchProvider,
  accessHiSearchProvider,
  accessNavigationSearchProvider,
  accessDashboardsSearchProvider,
  accessTqlSearchProvider,
  attributeSearchProvider,
  userAttributeSearchProvider,
  healthIndicatorSearchProvider,
  userSearchProvider,
  citSearchProvider,
  enrichmentSearchProvider,
  exporterSearchProvider,
  jobSearchProvider,
  vmAgentSearchProvider,
  vmAlertSearchProvider,
  monitoringTemplateSearchProvider,
  ldapSearchProvider,
  oidcSearchProvider,
  receiverSearchProvider,
  timeIntervalSearchProvider,
  senderSearchProvider,
  componentSearchProvider,
  pluginSearchProvider,
  globalSettingsSearchProvider,
];